import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { CircleCheck, Clock, LayoutDashboard, Plus, Sparkles, TriangleAlert, Truck, Users } from 'lucide-react';
import { useId, useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { fileteDasAbas } from '@/componentes/filete';
import { Janela } from '@/componentes/JanelaDeAcao';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeDeContagem, RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDataHora, formatarDia, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoPiloto } from './api';
import {
  contextoDoValor,
  destinoNaMesa,
  escreverValor,
  faltaNaEntrada,
  grupoEscrito,
  hojeNoCampo,
  periodoEscrito,
  PERIODOS_DO_PILOTO,
  retornoDaEntrada,
  retornoDaSaida,
  TELA_DA_MEDIDA,
  validarBuscaDoPiloto,
  type AbaDoPiloto,
  type EntradaNoPiloto,
  type IndicadorDoPiloto,
  type MaquinaDaCarga,
  type MedidaDoPiloto,
  type PainelDoPiloto,
  type PassagemDaMaquina,
  type PassagemDaPessoa,
  type PeriodoDoPiloto,
  type RespostaDaEntrada,
  type RespostaDaSaida,
  type SaidaDoPiloto,
  type TipoDoGrupo,
} from './piloto';

const rota = getRouteApi('/painel/piloto');

const ABAS: readonly (readonly [AbaDoPiloto, string])[] = [
  ['indicadores', 'Indicadores'],
  ['carga', 'Carga inicial'],
  ['grupo', 'Grupo do piloto'],
];

/** Uma linha da tabela dos indicadores: a medida e o item do §17.2 de onde ela vem. */
interface LinhaDoIndicador extends MedidaDoPiloto {
  readonly indicador: IndicadorDoPiloto;
}

/**
 * Módulo 21 — o PILOTO CONTROLADO (guia §17; critério §10.2).
 *
 * Três abas, e uma pergunta em cada: os sete indicadores do §17.2 (o grupo do
 * piloto ao lado da empresa inteira, com a definição e a meta de cada um), a
 * carga inicial (§17.1: o que falta para cada máquina operar no sistema, e a
 * tela que resolve) e o grupo (quem está no piloto, de quando a quando).
 *
 * Nenhum número é inventado: sem dado, o travessão e o porquê. A definição de
 * cada indicador vem do servidor, junto do número — a tela não escreve a sua.
 */
export function PaginaPiloto() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('piloto.ler');
  const gere = permissoes.includes('piloto.gerir');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const aba: AbaDoPiloto = busca.aba ?? 'indicadores';
  const dias: PeriodoDoPiloto = busca.dias ?? 30;
  const [entrando, setEntrando] = useState(false);
  /* A linha aberta (o indicador ou a máquina): o painel de detalhe mora AO
     LADO da lista (p.29), fora do contêiner que rola, e por isso o estado
     sobe para a página. Trocar de aba ou de filtro fecha. */
  const [aberta, setAberta] = useState<string | null>(null);

  function irPara(estado: { readonly aba?: AbaDoPiloto; readonly dias?: PeriodoDoPiloto; readonly grupo?: 'piloto' | 'todas'; readonly pendencia?: string; readonly pagina?: number }) {
    const grupo = 'grupo' in estado ? estado.grupo : busca.grupo;
    const pendencia = 'pendencia' in estado ? estado.pendencia : busca.pendencia;
    if ((estado.aba && estado.aba !== aba) || 'grupo' in estado || 'pendencia' in estado) setAberta(null);
    void navegar({
      search: validarBuscaDoPiloto({
        aba: estado.aba ?? aba,
        dias: estado.dias ?? dias,
        ...(grupo === 'piloto' ? { grupo } : {}),
        ...(pendencia ? { pendencia } : {}),
        pagina: estado.pagina ?? 1,
      }),
    });
  }

  if (sessao.data && !pode) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina sobretitulo="Painel" titulo="Piloto" />
          <EstadoVazio
            icone={LayoutDashboard}
            titulo="Você não tem acesso ao piloto"
            descricao="O piloto mostra o grupo, a carga inicial e os indicadores do guia. Peça ao administrador da empresa o perfil Gestor operacional ou Diretoria, que veem o piloto."
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Painel"
            titulo="Piloto"
            subtitulo="O grupo do piloto, a carga inicial e os sete indicadores do guia (§17), ao lado da empresa inteira"
            acoes={
              aba === 'indicadores' ? (
                <Seletor<`${PeriodoDoPiloto}`>
                  rotulo="Período"
                  valor={`${dias}`}
                  opcoes={PERIODOS_DO_PILOTO.map((d) => ({ valor: `${d}` as `${PeriodoDoPiloto}`, rotulo: `${String(d)} dias` }))}
                  aoEscolher={(v) => {
                    irPara({ dias: Number(v) as PeriodoDoPiloto });
                  }}
                />
              ) : aba === 'grupo' && gere ? (
                <Botao
                  nivel="primario"
                  onClick={() => {
                    setEntrando(true);
                  }}
                >
                  <Plus aria-hidden="true" className="size-4" />
                  Pôr no piloto
                </Botao>
              ) : null
            }
          />

          <div role="tablist" aria-label="Seções do piloto" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
            {ABAS.map(([id, rotulo]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={aba === id}
                onClick={() => {
                  irPara({ aba: id, pagina: 1 });
                }}
                className="aba"
              >
                {rotulo}
              </button>
            ))}
          </div>

          {aba === 'indicadores' ? <Indicadores dias={dias} aberta={aberta} aoAbrir={setAberta} /> : null}
          {aba === 'carga' ? (
            <CargaInicial
              grupo={busca.grupo ?? 'todas'}
              pendencia={busca.pendencia}
              pagina={busca.pagina ?? 1}
              aberta={aberta}
              aoAbrir={setAberta}
              aoFiltrar={(f) => {
                irPara({ ...f, pagina: 1 });
              }}
              aoPaginar={(pagina) => {
                irPara({ pagina });
              }}
            />
          ) : null}
          {aba === 'grupo' ? <Grupo gere={gere} entrando={entrando} aoFecharEntrada={() => { setEntrando(false); }} /> : null}
        </div>
      </div>
      {aba === 'indicadores' && aberta ? <PainelDoIndicador key={aberta} id={aberta} dias={dias} aoFechar={() => { setAberta(null); }} /> : null}
      {aba === 'carga' && aberta ? (
        <PainelDaMaquina
          key={aberta}
          id={aberta}
          grupo={busca.grupo ?? 'todas'}
          pendencia={busca.pendencia}
          pagina={busca.pagina ?? 1}
          aoFechar={() => { setAberta(null); }}
        />
      ) : null}
    </>
  );
}

/* ================================================================ indicadores */

function usePainelDoPiloto(dias: PeriodoDoPiloto) {
  return useQuery({
    queryKey: ['piloto', 'painel', dias],
    queryFn: () => apiDoPiloto.painel(dias),
    meta: { oQueLia: 'os indicadores do piloto' },
  });
}

function Indicadores({ dias, aberta, aoAbrir }: { readonly dias: PeriodoDoPiloto; readonly aberta: string | null; readonly aoAbrir: (id: string) => void }) {
  const painel = usePainelDoPiloto(dias);
  const setAberta = aoAbrir;
  const p = painel.data;
  const medida = (id: string): MedidaDoPiloto | undefined => p?.indicadores.flatMap((i) => i.medidas).find((m) => m.id === id);
  const linhas: readonly LinhaDoIndicador[] = p ? p.indicadores.flatMap((indicador) => indicador.medidas.map((m) => ({ ...m, indicador }))) : [];

  if (painel.isError && !p) {
    return (
      <EstadoVazio
        tom="erro"
        titulo="Não foi possível medir o piloto"
        descricao={`${explicarFalha(painel.error).erro}. ${explicarFalha(painel.error).comoResolver}`}
        acao={<Botao nivel="secundario" onClick={() => { void painel.refetch(); }}>Medir de novo</Botao>}
      />
    );
  }

  const cartao = (id: string, icone: typeof Truck, tom: 'marca' | 'operando' | 'disponivel' | 'standby', rotulo: string) => {
    const m = medida(id);
    return (
      <CardIndicador
        carregando={!m}
        icone={icone}
        tom={tom}
        rotulo={rotulo}
        valor={m ? escreverValor(m.grupo, m.unidade) : ''}
        alerta={m?.grupo.naMeta === false}
        contexto={m ? `${contextoDoValor(m.grupo)} · meta: ${m.meta.texto}` : ''}
        {...(m && m.unidade === 'porcento' && m.grupo.valor !== null ? { proporcao: m.grupo.valor } : {})}
        aoAbrir={() => {
          setAberta(id);
        }}
      />
    );
  };

  const colunas: readonly Coluna<LinhaDoIndicador>[] = [
    {
      chave: 'medida',
      rotulo: 'Indicador',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{l.rotulo}</span>
          <TextoDaCelula texto={l.indicador.titulo} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'grupo',
      rotulo: 'Grupo do piloto',
      alinhamento: 'numero',
      celula: (l) => <ValorNaCelula medida={l} conjunto="grupo" />,
    },
    {
      chave: 'empresa',
      rotulo: 'Empresa inteira',
      alinhamento: 'numero',
      celula: (l) => <ValorNaCelula medida={l} conjunto="empresa" />,
    },
    {
      chave: 'meta',
      rotulo: 'Meta',
      prioridade: 'secundaria',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{l.meta.texto}</span>
          <TextoDaCelula texto={l.meta.fonte.startsWith('guia') ? 'do guia' : 'recomendada'} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
  ];

  return (
    <>
      <FaixaIndicadores>
        {cartao('cadastro_completo', Truck, 'marca', 'Cadastro mínimo completo')}
        {cartao('apontamento_no_prazo', Clock, 'standby', 'Turnos apontados no prazo')}
        {cartao('pessoas_ativas', Users, 'disponivel', 'Pessoas do piloto ativas')}
        {cartao('ia_util', Sparkles, 'operando', 'Respostas da IA que serviram')}
      </FaixaIndicadores>

      {p ? (
        <p className="txt-apoio text-tinta-secundaria">
          {`${grupoEscrito(p.grupo)} · ${periodoEscrito(p)} · medido em ${formatarDataHora(new Date(p.geradoEm))}. Os cartões são do grupo do piloto; a tabela põe a empresa inteira ao lado. Toque numa linha para ler a definição.`}
        </p>
      ) : (
        <span className="esqueleto block h-4 w-2/3 max-w-full" aria-hidden="true" />
      )}

      <div data-painel-origem>
        <Tabela
          rotulo="Os sete indicadores do piloto"
          colunas={colunas}
          linhas={linhas}
          alturaDupla
          chaveDaLinha={(l) => l.id}
          carregando={painel.isPending}
          erro={
            painel.isError ? (
              <EstadoVazio
                tom="erro"
                titulo="Não foi possível atualizar os indicadores"
                descricao={`${explicarFalha(painel.error).erro}. ${explicarFalha(painel.error).comoResolver}`}
                acao={<Botao nivel="secundario" onClick={() => { void painel.refetch(); }}>Medir de novo</Botao>}
              />
            ) : null
          }
          selecionada={aberta}
          aoSelecionar={(l) => {
            setAberta(l.id);
          }}
          vazio={
            <EstadoVazio
              icone={LayoutDashboard}
              titulo="Nenhum indicador veio do servidor"
              descricao="O servidor respondeu sem os indicadores. Recarregue a página; se continuar, avise o suporte com o horário."
            />
          }
          rodape={<RodapeDeContagem total={linhas.length} />}
        />
      </div>

    </>
  );
}

function ValorNaCelula({ medida, conjunto }: { readonly medida: MedidaDoPiloto; readonly conjunto: 'grupo' | 'empresa' }) {
  const v = medida[conjunto];
  return (
    <span className="flex flex-col items-end">
      <span className={`txt-corpo tabular-nums ${v.naMeta === false ? 'text-erro-texto' : v.valor === null ? 'text-tinta-secundaria' : 'text-tinta'}`}>
        {escreverValor(v, medida.unidade)}
      </span>
      <TextoDaCelula texto={v.valor === null ? 'Sem dado: abra para ver por quê' : v.base} classe="txt-apoio text-tinta-apagada" linhas={1} />
    </span>
  );
}

/** O detalhe do indicador aberto, ao lado da lista: lê a mesma consulta da tabela. */
function PainelDoIndicador({ id, dias, aoFechar }: { readonly id: string; readonly dias: PeriodoDoPiloto; readonly aoFechar: () => void }) {
  const painel = usePainelDoPiloto(dias);
  const p = painel.data;
  const indicador = p?.indicadores.find((i) => i.medidas.some((m) => m.id === id));
  const medida = indicador?.medidas.find((m) => m.id === id);
  if (!p || !indicador || !medida) return null;
  return <DetalheDoIndicador linha={{ ...medida, indicador }} painel={p} aoFechar={aoFechar} />;
}

function DetalheDoIndicador({ linha, painel, aoFechar }: { readonly linha: LinhaDoIndicador; readonly painel: PainelDoPiloto; readonly aoFechar: () => void }) {
  const tela = TELA_DA_MEDIDA[linha.id];
  const semUso = linha.extra?.perfisDoGrupoSemUso ?? [];
  return (
    <PainelDetalhe
      titulo={linha.rotulo}
      subtitulo={`${linha.indicador.titulo} · ${periodoEscrito(painel)}`}
      dadosChave={
        <>
          <DadoChave rotulo="Grupo do piloto">{`${escreverValor(linha.grupo, linha.unidade)} · ${contextoDoValor(linha.grupo)}`}</DadoChave>
          <DadoChave rotulo="Empresa inteira">{`${escreverValor(linha.empresa, linha.unidade)} · ${contextoDoValor(linha.empresa)}`}</DadoChave>
          <DadoChave rotulo="Meta">{linha.meta.texto}</DadoChave>
          <DadoChave rotulo="De onde vem a meta">{linha.meta.fonte}</DadoChave>
        </>
      }
      {...(tela
        ? {
            acoes: [
              <Link key="ver" to={tela.mesa.to} {...(tela.mesa.search ? { search: tela.mesa.search } : {})} className="botao botao-secundario">
                {tela.rotulo}
              </Link>,
            ],
          }
        : {})}
      abas={[
        {
          id: 'definicao',
          rotulo: 'Como se conta',
          conteudo: (
            <div className="flex flex-col gap-3">
              <p className="txt-corpo text-pretty text-tinta">{linha.definicao}</p>
              {linha.id === 'tempo_para_abrir_os' && (linha.extra?.semInstante ?? 0) > 0 ? (
                <p className="txt-apoio text-tinta-secundaria">
                  {`${formatarInteiro(linha.extra?.semInstante ?? 0)} O.S. do período sem o instante do fato ficaram fora da mediana.`}
                </p>
              ) : null}
              {linha.id === 'pessoas_ativas' && semUso.length > 0 ? (
                <p className="txt-apoio text-erro-texto">{`Perfis do grupo sem ninguém ativo no período: ${semUso.join(', ')}.`}</p>
              ) : null}
            </div>
          ),
        },
        ...(linha.id === 'pessoas_ativas'
          ? [
              {
                id: 'perfis',
                rotulo: 'Por perfil',
                conteudo: <PorPerfil painel={painel} />,
              },
            ]
          : []),
      ]}
      aoFechar={aoFechar}
    />
  );
}

function PorPerfil({ painel }: { readonly painel: PainelDoPiloto }) {
  if (painel.porPerfil.length === 0) {
    return <EstadoVazio titulo="Nenhuma pessoa ativa cadastrada" descricao="Cadastre as pessoas em Perfis e responsáveis e dê a elas um papel." />;
  }
  return (
    <ul className="flex flex-col">
      {painel.porPerfil.map((l) => (
        <li key={l.perfil ?? 'sem'} className="flex items-center justify-between gap-3 border-b border-borda-sutil py-2">
          <span className="txt-corpo text-tinta">{l.perfil ?? 'Sem papel'}</span>
          <span className="txt-apoio text-right tabular-nums text-tinta-secundaria">
            {`${formatarInteiro(l.ativas)} de ${formatarInteiro(l.pessoas)} ${l.pessoas === 1 ? 'ativa' : 'ativas'}`}
            {l.pessoasNoGrupo > 0 ? ` · no grupo, ${formatarInteiro(l.ativasNoGrupo)} de ${formatarInteiro(l.pessoasNoGrupo)}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================ carga inicial */

function useCargaInicial(grupo: 'piloto' | 'todas', pendencia: string | undefined, pagina: number) {
  return useQuery({
    queryKey: ['piloto', 'carga', grupo, pendencia ?? '', pagina],
    queryFn: () => apiDoPiloto.carga(grupo, pendencia, pagina),
    meta: { oQueLia: 'a carga inicial' },
  });
}

function CargaInicial({
  grupo,
  pendencia,
  pagina,
  aberta,
  aoAbrir,
  aoFiltrar,
  aoPaginar,
}: {
  readonly grupo: 'piloto' | 'todas';
  readonly pendencia: string | undefined;
  readonly pagina: number;
  readonly aberta: string | null;
  readonly aoAbrir: (id: string) => void;
  readonly aoFiltrar: (f: { readonly grupo?: 'piloto' | 'todas'; readonly pendencia?: string }) => void;
  readonly aoPaginar: (pagina: number) => void;
}) {
  const carga = useCargaInicial(grupo, pendencia, pagina);
  const setAberta = aoAbrir;
  /* O atalho para importar a frota só para quem a cadastra (`equipamento.gerir`). */
  const sessao = useSessao();
  const podeImportarFrota = (sessao.data?.permissoes ?? []).includes('equipamento.gerir');
  const c = carga.data;
  const tipos = c?.tipos ?? {};
  const itens = c?.itens ?? [];
  const pendenciasDaEmpresa = (c?.empresa ?? []).reduce((n, t) => n + t.itens.length, 0);

  const chips: readonly ChipFiltro[] = [
    ...(grupo === 'piloto' ? [{ id: 'grupo', rotulo: 'Só o piloto', aoRemover: () => { aoFiltrar({ grupo: 'todas' }); } }] : []),
    ...(pendencia ? [{ id: 'pendencia', rotulo: `Pendência: ${tipos[pendencia]?.rotulo ?? pendencia}`, aoRemover: () => { aoFiltrar({ pendencia: '' }); } }] : []),
  ];

  const colunas: readonly Coluna<MaquinaDaCarga>[] = [
    {
      chave: 'maquina',
      rotulo: 'Máquina',
      prioridade: 'chave',
      celula: (m) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{m.codigo}</span>
          <TextoDaCelula texto={m.descricao} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'modelo', rotulo: 'Modelo', prioridade: 'secundaria', celula: (m) => m.modelo ?? <span className="text-tinta-secundaria">Sem modelo</span> },
    {
      chave: 'pendencias',
      rotulo: 'O que falta',
      celula: (m) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{`${formatarInteiro(m.pendencias.length)} ${m.pendencias.length === 1 ? 'pendência' : 'pendências'}`}</span>
          <TextoDaCelula texto={m.pendencias.map((p) => tipos[p]?.rotulo ?? p).join(' · ')} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'piloto',
      rotulo: 'Piloto',
      prioridade: 'secundaria',
      celula: (m) => (m.noPiloto ? <span className="txt-legenda rounded-etiqueta bg-rebaixada px-2 py-1 text-tinta">No piloto</span> : <span className="text-tinta-secundaria">Fora</span>),
    },
  ];

  if (carga.isError && !c) {
    return (
      <EstadoVazio
        tom="erro"
        titulo="Não foi possível ler a carga inicial"
        descricao={`${explicarFalha(carga.error).erro}. ${explicarFalha(carga.error).comoResolver}`}
        acao={<Botao nivel="secundario" onClick={() => { void carga.refetch(); }}>Ler de novo</Botao>}
      />
    );
  }

  return (
    <>
      <FaixaIndicadores>
        <CardIndicador
          carregando={!c}
          icone={CircleCheck}
          tom="operando"
          rotulo="Máquinas prontas"
          valor={c ? formatarInteiro(c.resumo.completas) : ''}
          contexto={c ? `de ${formatarInteiro(c.resumo.maquinas)} ${grupo === 'piloto' ? 'no piloto' : 'ativas'}, sem nenhuma pendência` : ''}
          {...(c && c.resumo.maquinas > 0 ? { proporcao: c.resumo.completas / c.resumo.maquinas } : {})}
        />
        <CardIndicador
          carregando={!c}
          icone={Truck}
          tom="marca"
          rotulo="No piloto, prontas"
          valor={c ? (c.resumo.noPiloto === 0 ? '—' : formatarInteiro(c.resumo.completasNoPiloto)) : ''}
          alerta={c ? c.resumo.noPiloto > 0 && c.resumo.completasNoPiloto < c.resumo.noPiloto : false}
          contexto={c ? (c.resumo.noPiloto === 0 ? 'Nenhuma máquina no piloto hoje. Marque o grupo na aba Grupo do piloto.' : `de ${formatarInteiro(c.resumo.noPiloto)} no piloto · meta: todas`) : ''}
        />
        <CardIndicador
          carregando={!c}
          icone={TriangleAlert}
          tom="standby"
          rotulo="Pendências fora da máquina"
          valor={c ? formatarInteiro(pendenciasDaEmpresa) : ''}
          alerta={pendenciasDaEmpresa > 0}
          contexto="tanque sem saldo, depósito sem posição, pessoa sem papel e função sem valor-hora"
        />
      </FaixaIndicadores>

      <BarraFiltros
        chips={chips}
        contagem={carga.isPending ? null : `${formatarInteiro(c?.total ?? 0)} ${(c?.total ?? 0) === 1 ? 'máquina com pendência' : 'máquinas com pendência'}`}
        aoLimpar={() => {
          aoFiltrar({ grupo: 'todas', pendencia: '' });
        }}
      >
        <CampoSelecao
          rotulo="Máquinas"
          valor={grupo === 'piloto' ? 'piloto' : ''}
          rotuloVazio="Todas as ativas"
          aplicado={grupo === 'piloto'}
          opcoes={[{ valor: 'piloto', rotulo: 'Só as do piloto' }]}
          aoMudar={(v) => {
            aoFiltrar({ grupo: v === 'piloto' ? 'piloto' : 'todas' });
          }}
        />
        <CampoSelecao
          rotulo="Pendência"
          valor={pendencia ?? ''}
          rotuloVazio="Todas"
          aplicado={Boolean(pendencia)}
          opcoes={Object.values(tipos).map((t) => ({
            valor: t.id,
            rotulo: `${t.rotulo} (${formatarInteiro(c?.resumo.porPendencia[t.id] ?? 0)})`,
          }))}
          aoMudar={(v) => {
            aoFiltrar({ pendencia: v });
          }}
        />
      </BarraFiltros>

      <div data-painel-origem>
        <Tabela
          rotulo="Máquinas com pendência na carga inicial"
          colunas={colunas}
          linhas={itens}
          alturaDupla
          chaveDaLinha={(m) => m.id}
          carregando={carga.isPending}
          erro={
            carga.isError ? (
              <EstadoVazio
                tom="erro"
                titulo="Não foi possível atualizar a carga inicial"
                descricao={`${explicarFalha(carga.error).erro}. ${explicarFalha(carga.error).comoResolver}`}
                acao={<Botao nivel="secundario" onClick={() => { void carga.refetch(); }}>Ler de novo</Botao>}
              />
            ) : null
          }
          selecionada={aberta}
          aoSelecionar={(m) => {
            setAberta(m.id);
          }}
          vazio={
            chips.length > 0 ? (
              <EstadoVazio
                titulo="Nenhuma máquina com esses filtros"
                descricao="Limpe os filtros ou escolha outra pendência."
                acao={<Botao nivel="secundario" onClick={() => { aoFiltrar({ grupo: 'todas', pendencia: '' }); }}>Limpar filtros</Botao>}
              />
            ) : (c?.resumo.maquinas ?? 0) === 0 ? (
              <EstadoVazio
                icone={Truck}
                titulo="Nenhuma máquina ativa cadastrada"
                descricao={
                  podeImportarFrota
                    ? 'A carga inicial começa pela frota. Cadastre as máquinas ou importe a planilha em Equipamentos.'
                    : 'A carga inicial começa pela frota, que o perfil Administrador cadastra ou importa em Equipamentos.'
                }
                acao={podeImportarFrota ? <Link to="/equipamentos/importar" className="botao botao-secundario">Importar frota</Link> : undefined}
              />
            ) : (
              <EstadoVazio
                icone={CircleCheck}
                titulo="Todas as máquinas estão prontas"
                descricao="Nenhuma máquina ativa tem pendência no cadastro mínimo. Confira abaixo o que falta fora das máquinas."
              />
            )
          }
          rodape={
            c && c.total > 0 ? (
              <RodapeTabela
                primeiro={(pagina - 1) * c.porPagina + 1}
                ultimo={(pagina - 1) * c.porPagina + itens.length}
                total={c.total}
                pagina={pagina}
                paginas={Math.max(Math.ceil(c.total / c.porPagina), 1)}
                porPagina={c.porPagina}
                opcoesPorPagina={[c.porPagina]}
                aoMudarPagina={aoPaginar}
                aoMudarPorPagina={() => undefined}
              />
            ) : null
          }
        />
      </div>

      <section className="flex flex-col gap-3" aria-labelledby="pendencias-da-empresa">
        <h2 id="pendencias-da-empresa" className="txt-secao text-tinta">O que falta fora das máquinas?</h2>
        {!c ? (
          <span className="esqueleto block h-24 rounded-card" aria-hidden="true" />
        ) : pendenciasDaEmpresa === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">Nada: todo tanque tem saldo, todo depósito tem posição, toda pessoa ativa tem papel e toda função exercida tem valor-hora.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {c.empresa
              .filter((t) => t.itens.length > 0)
              .map((t) => {
                const destino = destinoNaMesa(t.onde);
                return (
                  <li key={t.id} className="cartao flex flex-col gap-2 p-4">
                    <span className="txt-corpo text-tinta">{`${t.rotulo} · ${formatarInteiro(t.itens.length)}`}</span>
                    <span className="txt-apoio text-pretty text-tinta-secundaria">{t.oQueFazer}</span>
                    <span className="txt-apoio text-tinta-secundaria">
                      {t.itens.slice(0, 6).map((i) => (i.codigo ? `${i.codigo} · ${i.nome}` : i.nome)).join(', ')}
                      {t.itens.length > 6 ? ` e mais ${formatarInteiro(t.itens.length - 6)}` : ''}
                    </span>
                    <Link to={destino.to} className="botao botao-fantasma botao-p self-start">
                      {destino.rotulo}
                    </Link>
                  </li>
                );
              })}
          </ul>
        )}
      </section>

    </>
  );
}

/** O que falta na máquina aberta, ao lado da lista: lê a mesma consulta da tabela. */
function PainelDaMaquina({
  id,
  grupo,
  pendencia,
  pagina,
  aoFechar,
}: {
  readonly id: string;
  readonly grupo: 'piloto' | 'todas';
  readonly pendencia: string | undefined;
  readonly pagina: number;
  readonly aoFechar: () => void;
}) {
  const carga = useCargaInicial(grupo, pendencia, pagina);
  const c = carga.data;
  const maquina = c?.itens.find((m) => m.id === id);
  if (!c || !maquina) return null;
  return (
    <PainelDetalhe
      key={maquina.id}
      codigo={maquina.codigo}
      titulo={maquina.descricao}
      subtitulo={maquina.modelo ?? 'Sem modelo'}
      dadosChave={
        <>
          <DadoChave rotulo="Pendências">{formatarInteiro(maquina.pendencias.length)}</DadoChave>
          <DadoChave rotulo="Piloto">{maquina.noPiloto ? 'No piloto hoje' : 'Fora do piloto'}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'falta',
          rotulo: 'O que falta',
          conteudo: (
            <ol className="flex flex-col gap-3">
              {maquina.pendencias.map((pendenciaId) => {
                const t = c.tipos[pendenciaId];
                if (!t) return null;
                const destino = destinoNaMesa(t.onde, maquina);
                return (
                  <li key={pendenciaId} className="flex flex-col gap-1 border-b border-borda-sutil pb-3">
                    <span className="txt-corpo text-tinta">{t.rotulo}</span>
                    <span className="txt-apoio text-pretty text-tinta-secundaria">{t.oQueFazer}</span>
                    <Link to={destino.to} {...(destino.search ? { search: destino.search } : {})} className="botao botao-fantasma botao-p self-start">
                      {destino.rotulo}
                    </Link>
                  </li>
                );
              })}
            </ol>
          ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

/* ================================================================== o grupo */

function Grupo({ gere, entrando, aoFecharEntrada }: { readonly gere: boolean; readonly entrando: boolean; readonly aoFecharEntrada: () => void }) {
  const grupo = useQuery({ queryKey: ['piloto', 'grupo'], queryFn: () => apiDoPiloto.grupo(), meta: { oQueLia: 'o grupo do piloto' } });
  const [saindo, setSaindo] = useState<{ readonly tipo: TipoDoGrupo; readonly id: string; readonly quem: string; readonly entrada: string } | null>(null);
  const g = grupo.data;

  const acaoDeSaida = (tipo: TipoDoGrupo, id: string, quem: string, entrada: string, aberta: boolean) =>
    gere && aberta ? (
      <Botao
        nivel="fantasma"
        tamanho="p"
        onClick={() => {
          setSaindo({ tipo, id, quem, entrada });
        }}
      >
        Registrar saída
      </Botao>
    ) : null;

  const colunasDasMaquinas: readonly Coluna<PassagemDaMaquina>[] = [
    {
      chave: 'maquina',
      rotulo: 'Máquina',
      prioridade: 'chave',
      celula: (m) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{m.codigo}</span>
          <TextoDaCelula texto={[m.descricao, m.modelo].filter(Boolean).join(' · ')} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'entrada', rotulo: 'Entrada', celula: (m) => <span className="tabular-nums">{formatarDia(m.entrada)}</span> },
    {
      chave: 'saida',
      rotulo: 'Último dia',
      celula: (m) => (m.saida ? <span className="tabular-nums">{formatarDia(m.saida)}</span> : <span className="text-tinta-secundaria">No grupo</span>),
    },
    {
      chave: 'vencidas',
      rotulo: 'Vencidas na entrada',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (m) => (m.vencidasNaEntrada === null ? <span className="text-tinta-secundaria">Sem plano</span> : formatarInteiro(m.vencidasNaEntrada)),
    },
  ];
  const colunasDasPessoas: readonly Coluna<PassagemDaPessoa>[] = [
    {
      chave: 'pessoa',
      rotulo: 'Pessoa',
      prioridade: 'chave',
      celula: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{p.nome}</span>
          <TextoDaCelula texto={p.perfil ?? 'Sem papel'} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'entrada', rotulo: 'Entrada', celula: (p) => <span className="tabular-nums">{formatarDia(p.entrada)}</span> },
    {
      chave: 'saida',
      rotulo: 'Último dia',
      celula: (p) => (p.saida ? <span className="tabular-nums">{formatarDia(p.saida)}</span> : <span className="text-tinta-secundaria">No grupo</span>),
    },
  ];

  const erro = grupo.isError ? (
    <EstadoVazio
      tom="erro"
      titulo="Não foi possível ler o grupo do piloto"
      descricao={`${explicarFalha(grupo.error).erro}. ${explicarFalha(grupo.error).comoResolver}`}
      acao={<Botao nivel="secundario" onClick={() => { void grupo.refetch(); }}>Ler de novo</Botao>}
    />
  ) : null;
  const vazio = (oQue: 'máquina' | 'pessoa') => (
    <EstadoVazio
      icone={oQue === 'máquina' ? Truck : Users}
      titulo={oQue === 'máquina' ? 'Nenhuma máquina no piloto' : 'Nenhuma pessoa no piloto'}
      descricao={
        gere
          ? `O guia pede uma frota pequena e representativa, e as pessoas que vão usá-la (§17.1). Use Pôr no piloto para marcar a primeira ${oQue}.`
          : 'Quem conduz o piloto marca as máquinas e as pessoas. Peça ao Gestor operacional.'
      }
    />
  );

  return (
    <>
      <p className="txt-apoio text-tinta-secundaria">
        Quem está no piloto, de quando a quando. Um registro conta para o piloto quando a máquina, ou a pessoa, estava no grupo naquele dia. Sair não apaga nada: fecha a passagem, e o que veio antes continua contando.
      </p>
      <section className="flex flex-col gap-3" aria-labelledby="maquinas-do-piloto">
        <h2 id="maquinas-do-piloto" className="txt-secao text-tinta">Que máquinas estão no piloto?</h2>
        <Tabela
          rotulo="Máquinas do piloto"
          colunas={colunasDasMaquinas}
          linhas={g?.maquinas ?? []}
          chaveDaLinha={(m) => m.id}
          alturaDupla
          carregando={grupo.isPending}
          erro={erro}
          vazio={vazio('máquina')}
          acoes={(m) => acaoDeSaida('maquina', m.id, m.codigo, m.entrada, m.saida === null)}
          rodape={<RodapeDeContagem total={g?.maquinas.length ?? 0} />}
        />
      </section>
      <section className="flex flex-col gap-3" aria-labelledby="pessoas-do-piloto">
        <h2 id="pessoas-do-piloto" className="txt-secao text-tinta">Que pessoas estão no piloto?</h2>
        <Tabela
          rotulo="Pessoas do piloto"
          colunas={colunasDasPessoas}
          linhas={g?.pessoas ?? []}
          chaveDaLinha={(p) => p.id}
          alturaDupla
          carregando={grupo.isPending}
          erro={erro}
          vazio={vazio('pessoa')}
          acoes={(p) => acaoDeSaida('pessoa', p.id, p.nome, p.entrada, p.saida === null)}
          rodape={<RodapeDeContagem total={g?.pessoas.length ?? 0} />}
        />
      </section>

      {gere ? <JanelaDeEntrada aberta={entrando} aoFechar={aoFecharEntrada} /> : null}
      {gere ? (
        <JanelaDeSaida
          alvo={saindo}
          aoFechar={() => {
            setSaindo(null);
          }}
        />
      ) : null}
    </>
  );
}

const ENTRADA_VAZIA: EntradaNoPiloto = { tipo: 'maquina', id: '', entrada: hojeNoCampo(), observacao: '' };

function JanelaDeEntrada({ aberta, aoFechar }: { readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const base = useId();
  const [v, setV] = useState<EntradaNoPiloto>(ENTRADA_VAZIA);
  const [termo, setTermo] = useState('');
  const [tentou, setTentou] = useState(false);
  const candidatos = useQuery({
    queryKey: ['piloto', 'candidatos', v.tipo, termo.trim()],
    enabled: aberta,
    queryFn: () => apiDoPiloto.candidatos(v.tipo, termo.trim()),
    meta: { oQueLia: v.tipo === 'maquina' ? 'as máquinas' : 'as pessoas' },
  });
  const entrar = useMutation({
    mutationFn: (e: EntradaNoPiloto) => apiDoPiloto.entrar(e),
    meta: {
      retorno: retornoDe<RespostaDaEntrada, EntradaNoPiloto>((r, e) => ({
        titulo: e.tipo === 'maquina' ? 'Máquina posta no piloto' : 'Pessoa posta no piloto',
        ...retornoDaEntrada(r, e),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      fechar();
      void cliente.invalidateQueries({ queryKey: ['piloto'] });
    },
  });
  const falta = tentou ? faltaNaEntrada(v) : {};
  function fechar() {
    entrar.reset();
    setV({ ...ENTRADA_VAZIA, entrada: hojeNoCampo() });
    setTermo('');
    setTentou(false);
    aoFechar();
  }
  const lista = candidatos.data?.candidatos ?? [];
  return (
    <Janela
      titulo="Pôr no piloto"
      descricao="A máquina ou a pessoa entra no grupo a partir da data. Os indicadores contam o que ela fizer dali em diante."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={entrar.isPending}
            onClick={() => {
              setTentou(true);
              if (Object.keys(faltaNaEntrada(v)).length > 0) return;
              entrar.mutate(v);
            }}
          >
            {entrar.isPending ? 'Gravando…' : 'Pôr no piloto'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {entrar.isError ? (
          <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
            <span className="txt-corpo text-tinta">{explicarFalha(entrar.error).erro}</span>
            <span className="txt-apoio text-tinta-secundaria">{explicarFalha(entrar.error).comoResolver}</span>
          </div>
        ) : null}
        <Seletor<TipoDoGrupo>
          rotulo="O que entra"
          valor={v.tipo}
          opcoes={[
            { valor: 'maquina', rotulo: 'Máquina' },
            { valor: 'pessoa', rotulo: 'Pessoa' },
          ]}
          aoEscolher={(tipo) => {
            setV({ ...v, tipo, id: '' });
            setTermo('');
          }}
        />
        <CampoTexto
          rotulo={v.tipo === 'maquina' ? 'Buscar máquina' : 'Buscar pessoa'}
          valor={termo}
          aoMudar={setTermo}
          placeholder={v.tipo === 'maquina' ? 'ESC-001 ou 320D' : 'Nome ou e-mail'}
          ajuda="Mostra até 20. Escreva parte do código, do modelo ou do nome para achar a certa."
        />
        <div role="radiogroup" aria-label={v.tipo === 'maquina' ? 'Máquinas encontradas' : 'Pessoas encontradas'} className="flex max-h-60 flex-col overflow-auto rounded-controle border border-borda">
          {candidatos.isPending ? (
            <span className="esqueleto m-3 block h-10" aria-hidden="true" />
          ) : candidatos.isError ? (
            <p role="alert" className="txt-apoio p-3 text-erro-texto">{`${explicarFalha(candidatos.error).erro}. ${explicarFalha(candidatos.error).comoResolver}`}</p>
          ) : lista.length === 0 ? (
            <p className="txt-apoio p-3 text-tinta-secundaria">
              {termo.trim() ? `Nada com “${termo.trim()}”. Tente outra parte do código ou do nome.` : 'Nenhuma cadastrada ativa.'}
            </p>
          ) : (
            lista.map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={v.id === c.id}
                disabled={c.noGrupo}
                onClick={() => {
                  setV({ ...v, id: c.id });
                }}
                className={`flex min-h-12 items-center justify-between gap-3 border-b border-borda-sutil px-3 py-2 text-left last:border-b-0 ${v.id === c.id ? 'bg-marca-fundo' : ''} disabled:text-tinta-secundaria`}
              >
                <span className="flex min-w-0 flex-col">
                  <span className={v.tipo === 'maquina' ? 'codigo text-tinta' : 'txt-corpo text-tinta'}>{c.codigo ?? c.nome}</span>
                  <span className="txt-apoio truncate text-tinta-secundaria">
                    {v.tipo === 'maquina' ? [c.descricao, c.modelo].filter(Boolean).join(' · ') : (c.perfil ?? 'Sem papel')}
                  </span>
                </span>
                {c.noGrupo ? <span className="txt-legenda shrink-0 rounded-etiqueta bg-rebaixada px-2 py-1 text-tinta">Já no piloto</span> : null}
              </button>
            ))
          )}
        </div>
        {falta.id ? <p className="txt-ajuda text-erro">{falta.id}</p> : null}
        <Campo id={`${base}-entrada`} rotulo="Data de entrada" erro={falta.entrada ?? null} ajuda="Pode ser hoje ou outra data. Antes dela, nada conta para o piloto.">
          <input
            id={`${base}-entrada`}
            type="date"
            className="controle"
            value={v.entrada}
            onChange={(e) => {
              setV({ ...v, entrada: e.target.value });
            }}
          />
        </Campo>
        <CampoTexto
          rotulo="Observação"
          opcional
          valor={v.observacao}
          aoMudar={(observacao) => {
            setV({ ...v, observacao });
          }}
          placeholder="Modelo repetido, documentação completa"
          maxLength={300}
        />
      </div>
    </Janela>
  );
}

function JanelaDeSaida({
  alvo,
  aoFechar,
}: {
  readonly alvo: { readonly tipo: TipoDoGrupo; readonly id: string; readonly quem: string; readonly entrada: string } | null;
  readonly aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const base = useId();
  const [saida, setSaida] = useState(hojeNoCampo());
  const sair = useMutation({
    mutationFn: (s: SaidaDoPiloto) => apiDoPiloto.sair(s),
    meta: {
      retorno: retornoDe<RespostaDaSaida, SaidaDoPiloto>((r, s) => ({ titulo: 'Saída do piloto registrada', ...retornoDaSaida(r, s) })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      fechar();
      void cliente.invalidateQueries({ queryKey: ['piloto'] });
    },
  });
  function fechar() {
    sair.reset();
    setSaida(hojeNoCampo());
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Tirar ${alvo.quem} do piloto?` : 'Tirar do piloto?'}
      descricao="A passagem fecha no último dia. O que foi registrado até lá continua contando para o piloto; nada é apagado."
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Manter no piloto
          </Botao>
          <Botao
            nivel="primario"
            disabled={sair.isPending || !alvo}
            onClick={() => {
              if (alvo) sair.mutate({ tipo: alvo.tipo, id: alvo.id, saida });
            }}
          >
            {sair.isPending ? 'Gravando…' : 'Registrar saída'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {sair.isError ? (
          <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
            <span className="txt-corpo text-tinta">{explicarFalha(sair.error).erro}</span>
            <span className="txt-apoio text-tinta-secundaria">{explicarFalha(sair.error).comoResolver}</span>
          </div>
        ) : null}
        <Campo id={`${base}-saida`} rotulo="Último dia no piloto" ajuda={alvo ? `Entrou em ${formatarDia(alvo.entrada)}. Os registros deste dia ainda contam.` : 'Os registros deste dia ainda contam.'}>
          <input
            id={`${base}-saida`}
            type="date"
            className="controle"
            value={saida}
            onChange={(e) => {
              setSaida(e.target.value);
            }}
          />
        </Campo>
      </div>
    </Janela>
  );
}
